/**
 * SIEM / Audit – actions to investigate SMSWiser kill events in TrafficPulse.
 */

import { useMutation } from '@tanstack/react-query';
import { ExternalLink, ScrollText, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button, Tooltip } from '@/components/ui';
import { unwrapApiData } from '@/hooks/smswiser-campaign-workspace/utils';
import type { SmswiserKillContext } from '@/features/siem/utils/extract-smswiser-kill-context';
import {
  buildSmswiserPreparedInvestigationPath,
  type PreparedDropLookupPayload,
} from '@/features/siem/utils/build-smswiser-prepared-investigation-link';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import api from '@/services/api';

export type SiemInvestigationBarProps = {
  killContext: SmswiserKillContext;
  userEmail: string | null;
  canOpenWorkspace: boolean;
  canOpenUsers: boolean;
  canOpenAuditLogs: boolean;
};

export function SiemInvestigationBar({
  killContext,
  userEmail,
  canOpenWorkspace,
  canOpenUsers,
  canOpenAuditLogs,
}: SiemInvestigationBarProps) {
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const lookupMutation = useMutation({
    mutationFn: async () => {
      const params =
        killContext.wiserDropId != null
          ? { dropId: killContext.wiserDropId }
          : { uniqueId: killContext.uniqueId! };
      const res = await api.get('/smswiser/prepared-drops/lookup-by-sent-drop', { params });
      return unwrapApiData<PreparedDropLookupPayload>(res.data);
    },
    onSuccess: (data) => {
      navigate(buildSmswiserPreparedInvestigationPath(data));
    },
    onError: (err: unknown) => {
      const msg = getApiErrorMessage(err, 'Lookup failed');
      const s = typeof msg === 'string' ? msg : '';
      const is404 =
        s.includes('404') ||
        s.toLowerCase().includes('not found') ||
        s.toLowerCase().includes('no trafficpulse');
      toastError(
        is404 ? 'No linked prepared drop' : 'Lookup failed',
        is404
          ? 'This SMSWiser drop is not linked to a TrafficPulse prepared row (e.g. created outside the app).'
          : s || 'Request failed',
      );
    },
  });

  const auditSearch =
    userEmail && userEmail.trim().length > 0 ? userEmail.trim() : 'drop.delete';

  return (
    <div className="space-y-2 rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5">
      <p className="text-xs leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground">Kill investigation</span>
        {' — '}
        Link the SMSWiser drop id to the TrafficPulse prepared row (country / campaign). Admin
        shortcuts help review who acted and related audit entries.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Tooltip content="Look up this SMSWiser id in our DB and open the matching prepared drop in the country workspace.">
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={!canOpenWorkspace || lookupMutation.isPending}
            onClick={() => lookupMutation.mutate()}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Open in workspace
          </Button>
        </Tooltip>
        {canOpenUsers && userEmail ? (
          <Tooltip content="Opens Admin → Users with this email in the search box (roles, active state). Not related to SIEM “IP region” or SMSWiser country.">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/admin/users?search=${encodeURIComponent(userEmail)}`)
              }
            >
              <User className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Find user (admin)
            </Button>
          </Tooltip>
        ) : null}
        {canOpenAuditLogs ? (
          <Tooltip content="Opens Admin → Audit logs with search prefilled (actor email or “drop.delete”) to see nearby actions.">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/admin/audit-logs?search=${encodeURIComponent(auditSearch)}`)
              }
            >
              <ScrollText className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Audit trail (admin)
            </Button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
